import { HighlightOff, PlaylistAddCheck } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { useMotionValue } from "motion/react";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  AccessControl,
  FloatingMenuButton,
  FloatingMenuStyled,
  FloatingMenuTooltipWrapper,
  FloatingMenuTooltipWrapperIfDisabled,
  MuiButton,
  useFloatMenu,
} from "@ryde/controls";
import {
  useAiAdvisor,
  useModalConfirm,
  usePageContext,
  useRidesBulkActions,
  useSelectedIds,
} from "@ryde/redux";
import {
  ConfirmResult,
  Log,
  reportBulkOperationsDrawerAF,
  ReportTemplateIdEnum,
  useIsMobile,
  useSystemParams,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { RidesEditStatusDropdown } from "../../../../rides/search/panels/parts";

const MAX_RIDES_AI_ADVISOR_ROWS = 3;

export const RidesReportFloatingMenu: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const { enableAIFeatures } = useSystemParams();
  const { selectedRowsIds: selectedRideIds } = useSelectedIds(entity);

  const setRidesReportBulkOperationsDrawer = useSetRecoilState(
    reportBulkOperationsDrawerAF(ReportTemplateIdEnum.RIDE_ANALYTICS_REPORT),
  );

  const { confirmDialogResult } = useModalConfirm();

  const { bulkRebuildRoutes, bulkSendToSupplier } = useRidesBulkActions();

  const { clearSelectedIds } = useSelectedIds();
  const isOpened = !!selectedRideIds?.length;
  const {
    setBottomY,
    bottomY,
    isInitFinished,
    leftX,
    setLeftX,
    floatingMenuRef,
  } = useFloatMenu({ isOpened, page: "rides" });

  const left = useMotionValue(leftX);
  const bottom = useMotionValue(bottomY);

  const { openBulkAIAdvisorDrawer } = useAiAdvisor(entity);

  const handleRebuildRouteClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.REBUILD_ROUTES_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const result = await bulkRebuildRoutes(selectedRideIds);
      if (!result?.success) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.BULK_REBUILD_ROUTES_CANCELED`),
          selectedRideIds,
        );
      }
    }
  });

  const handleSendToSupplierClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.SEND_TO_SUPPLIER_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkSendToSupplier(selectedRideIds);
    }
  });

  const onDragEndHandler = useEvent((): void => {
    setLeftX(left.get());
    setBottomY(bottom.get());
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    left.set(leftX);
  }, [isOpened, left, leftX]);

  const isRidesAIAdvisorButtonDisabled =
    selectedRideIds.length > MAX_RIDES_AI_ADVISOR_ROWS;

  return (
    isOpened && (
      <FloatingMenuStyled
        ref={floatingMenuRef}
        drag
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={onDragEndHandler}
        style={{ x: left, bottom, opacity: !isInitFinished ? 0 : "initial" }}
      >
        {!isMobile && <SVG_MUI.GripVertical3SVG className="pointer grip" />}

        <Badge color="secondary" badgeContent={selectedRideIds?.length}>
          <PlaylistAddCheck style={{ color: greyMui._700 }} />
        </Badge>

        <FloatingMenuTooltipWrapper tooltip={t("ride.bulk.clear")}>
          <FloatingMenuButton onClick={clearSelectedIds}>
            <HighlightOff sx={{ color: greyMui._700 }} />
          </FloatingMenuButton>
        </FloatingMenuTooltipWrapper>

        {!isMobile && (
          <>
            <AccessControl entity="ride" action="rebuild">
              <FloatingMenuTooltipWrapper tooltip={t("general.REBUILD_ROUTES")}>
                <FloatingMenuButton onClick={handleRebuildRouteClick}>
                  <SVG_MUI.ArrowsRotate3SVG color={greyMui.A700} />
                  <span className="grey-text">{t(`general.REBUILD`)}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapper>
            </AccessControl>

            <AccessControl entity="ride" action="send_to_supplier">
              <FloatingMenuTooltipWrapper
                tooltip={t("general.SEND_TO_SUPPLIER")}
              >
                <FloatingMenuButton onClick={handleSendToSupplierClick}>
                  <SVG_MUI.SendSVG color={greyMui.A700} />
                  <span className="grey-text">{t(`general.SEND`)}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapper>
            </AccessControl>

            {enableAIFeatures && (
              <FloatingMenuTooltipWrapperIfDisabled
                tooltip={t("aiAdvisor.rideAiAdvisorRowsLimitedTooltip")}
                disabled={isRidesAIAdvisorButtonDisabled}
              >
                <FloatingMenuButton
                  disabled={isRidesAIAdvisorButtonDisabled}
                  onClick={openBulkAIAdvisorDrawer}
                >
                  <PIC.ErlenmeyerFlaskSVG />
                  <span className="grey-text">{t("aiAdvisor.analyze")}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapperIfDisabled>
            )}

            <RidesEditStatusDropdown />
          </>
        )}
        <MuiButton
          muiStyle="secondary"
          className="openDrawer"
          muiSize="small"
          onClick={() => setRidesReportBulkOperationsDrawer(true)}
        >
          <SVG_MUI.EllipsisVertical3SVG color="white" />
        </MuiButton>
      </FloatingMenuStyled>
    )
  );
};
